import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { onboarding } from '@cwos/api-client';
import { ErrorPanel, Icon, Pill, Refused, SkeletonSet } from '@cwos/ui-admin';
import { useNav } from '../navigation.js';
import { usePermissions } from '../permissions.js';
import { LoadFailure, refusalMessage } from '../LoadFailure.js';
import { SettingsHeader } from '../settings/common.js';
import {
  readProgress,
  sequenceState,
  stepGuide,
  writeOutcome,
  type StepKey,
  type StepProof,
  type WriteOutcome,
} from './onboardingLogic.js';

const STATUS_KEY = ['onboarding', 'status'] as const;

/**
 * Settings › Getting set up — the fourteen-step first run (§O9).
 *
 * The domain has been mounted at `server.ts` since Task 23 and its api-client
 * namespace had zero importers: a finished wire contract with no screen, which
 * is the state onboarding stays in longer than anything else because its
 * absence is invisible to everybody already inside the product.
 *
 * ─────────────────────────────────────────────────────────────────────────────
 * THREE PROPERTIES THIS SCREEN IS BUILT AROUND.
 *
 * 1. IT RESUMES. Somebody will close the tab, and the next person to open this
 *    may be a different administrator on a different machine a week later. So
 *    nothing about where the sequence stands is held in component state: the
 *    row states, the count, the percentage, the "next" step and the date of the
 *    last thing recorded all come from `GET /api/onboarding/status`, which reads
 *    the `onboarding_checklist` table. Reload it and it says the same thing.
 *
 * 2. IT NEVER CLAIMS A COMPLETION IT CANNOT SEE. `completeStep` returns the
 *    completed item and this screen deliberately does not paint the row from it.
 *    It invalidates the status query and repaints from the re-read. A write that
 *    was accepted and did not land therefore shows as `unconfirmed` — named, and
 *    not a checkmark. A "you're all set" computed from having pressed the
 *    buttons is precisely the claim that is wrong the one time it matters.
 *
 * 3. IT RUNS BEFORE THE READER HAS MUCH AUTHORITY. This is the worst screen in
 *    the product on which to tell a brand-new customer they lack access, because
 *    they have no way to know it is wrong and nobody senior to ask. So the
 *    advancing control is never rendered from an unresolved permission set —
 *    hidden while unknown (the safe direction), a plain sentence only once the
 *    set has actually arrived. See
 *    `docs/intelligence/audit/UNKNOWN-IS-NOT-DENIED-FRONTEND.md`; the route
 *    itself is wrapped in `RequirePermission`, which owns the three answers for
 *    `onboarding:read`.
 * ─────────────────────────────────────────────────────────────────────────────
 */
export function OnboardingChecklist() {
  const qc = useQueryClient();
  const { has, isLoading: permsLoading, isError: permsError } = usePermissions();

  /**
   * Not `enabled`-gated on `has('onboarding:write')` or on the read key.
   *
   * The route guard has already resolved `onboarding:read`, and gating the query
   * on a set that has not arrived is the `WorkerProfile.tsx:96` defect — a
   * screen that refuses AND fetches nothing, on a permission the reader holds.
   */
  const status = useQuery({
    queryKey: STATUS_KEY,
    queryFn: () => onboarding.status(),
  });

  /**
   * The last attempt, per step.
   *
   * Keyed by step rather than a single `mutation.error`, because the sentence
   * belongs beside the row it is about: a shared banner reading "couldn't save"
   * over fourteen rows says nothing about which one, and this screen's whole
   * job is to be precise about what is and is not recorded.
   */
  const [attempts, setAttempts] = useState<
    Record<string, { settled: boolean; error: unknown } | undefined>
  >({});

  const complete = useMutation({
    mutationFn: (step: StepKey) => onboarding.completeStep(step),
    onMutate: (step) => {
      setAttempts((prev) => ({ ...prev, [step]: { settled: false, error: undefined } }));
    },
    onSettled: async (_data, error, step) => {
      // Re-read BEFORE the row is allowed to change. The awaited invalidation is
      // what makes `unconfirmed` reachable: without it the row would settle
      // against a stale checklist and a lost write would look like a win.
      await qc.invalidateQueries({ queryKey: STATUS_KEY });
      setAttempts((prev) => ({ ...prev, [step]: { settled: true, error: error ?? undefined } }));
    },
  });

  if (status.isLoading) {
    return (
      <div className="rn-col" style={{ maxWidth: 780 }}>
        <SettingsHeader title="Getting set up" />
        <SkeletonSet surface="table" rows={6} />
      </div>
    );
  }

  // No answer at all. `LoadFailure` separates the refusal (an answer — no retry)
  // from the fault (retryable) from the ended session, which is the distinction
  // a bare "couldn't load" loses.
  if (status.data === undefined) {
    return (
      <div className="rn-col" style={{ maxWidth: 780 }}>
        <SettingsHeader title="Getting set up" />
        <LoadFailure
          error={status.error}
          message="We couldn’t read your setup progress just now. Do not read this as a checklist with nothing on it — we did not get an answer."
          refusedHeading="Setup progress is not yours to see"
          onRetry={() => void status.refetch()}
        />
      </div>
    );
  }

  const progress = readProgress(status.data);
  const sequence = sequenceState(status.data);
  // Resolved means the set actually arrived. `has` is false for an unheld key,
  // for a failed read and for a read still in flight; only the first of those
  // entitles this screen to say anything to the reader about their access.
  const permissionsResolved = !permsLoading && !permsError;
  const mayAdvance = permissionsResolved && has('onboarding:write');

  return (
    <div className="rn-col" style={{ maxWidth: 780 }}>
      <SettingsHeader
        title="Getting set up"
        blurb="The steps between a new Phibian organization and a live one. Everything here is read from your organization’s record — closing this page loses nothing."
      />

      {/* A failed REFRESH over an answer that did arrive. The figures stay, and
          say when they are from, rather than blanking a page somebody is
          working through. */}
      {status.isError ? (
        <p className="rn-muted" role="status" data-testid="onboarding-stale">
          This is the last answer we got. A refresh just failed, so something may have been recorded
          since.{' '}
          <button type="button" className="rn-link" onClick={() => void status.refetch()}>
            Try again
          </button>
        </p>
      ) : null}

      <ProgressCard progress={progress} sequence={sequence} orgId={status.data.orgId} />

      {sequence.kind === 'unstartable' ? <Unstartable /> : null}

      {permissionsResolved && !mayAdvance ? (
        <p className="rn-muted" role="status">
          You can see how setup stands, and you cannot advance it. Marking a step needs the{' '}
          <code>onboarding:write</code> permission, which whoever administers your organization can
          grant.
        </p>
      ) : null}

      <ol className="onb-steps">
        {status.data.checklist.map((item, index) => {
          const guide = stepGuide(item.step);
          const isNext = sequence.kind === 'in-progress' && sequence.next === item.step;
          const outcome = writeOutcome(attempts[item.step], item.completedAt !== null);
          return (
            <StepRow
              key={item.step}
              ordinal={index + 1}
              item={item}
              guide={guide}
              isNext={isNext}
              /* Only the outstanding step is offered, because only the
                 outstanding step can succeed: `completeStep` walks the sequence
                 and answers 422 PREREQUISITE_NOT_MET for anything ahead of it. A
                 button whose only outcome is a refusal is worse than no button. */
              mayAdvance={mayAdvance && isNext && guide?.proof !== 'automatic'}
              pending={complete.isPending && complete.variables === item.step}
              outcome={outcome}
              onAdvance={() => complete.mutate(item.step)}
            />
          );
        })}
      </ol>
    </div>
  );
}

// ---------------------------------------------------------------------------

function ProgressCard({
  progress,
  sequence,
  orgId,
}: {
  progress: ReturnType<typeof readProgress>;
  sequence: ReturnType<typeof sequenceState>;
  orgId: string;
}) {
  const nextGuide = sequence.kind === 'in-progress' ? stepGuide(sequence.next) : undefined;
  return (
    <section className="rn-card" aria-labelledby="onb-progress-title">
      <h2 className="rn-card-title" id="onb-progress-title">
        {sequence.kind === 'live' ? 'Your organization is live' : 'Where you left off'}
      </h2>
      {/* The server's figure, rendered as the server's figure. This screen does
          not count rows and call the result progress. */}
      <p className="rn-muted" style={{ marginTop: 'var(--space-6)' }}>
        {progress.done} of {progress.total} steps recorded ({progress.percent}%). This is your
        organization’s record, not this browser’s.
      </p>
      <div
        className="onb-meter"
        role="img"
        aria-label={`${progress.percent} percent of setup recorded`}
      >
        <span style={{ width: `${progress.percent}%` }} />
      </div>
      {progress.lastCompletedAt !== null && progress.lastCompletedStep !== null ? (
        <p className="rn-muted" style={{ marginTop: 'var(--space-10)' }}>
          Last recorded:{' '}
          <strong>
            {stepGuide(progress.lastCompletedStep)?.label ?? progress.lastCompletedStep}
          </strong>{' '}
          on {new Date(progress.lastCompletedAt).toLocaleDateString()}.
        </p>
      ) : (
        /* A REAL, MEASURED ZERO — and it says which kind of nothing it is. The
           read succeeded; the checklist is genuinely empty. */
        <p
          className="rn-muted"
          style={{ marginTop: 'var(--space-10)' }}
          data-testid="onb-nothing-yet"
        >
          Nothing has been recorded for this organization yet. That is an answer, not a failed read
          — the checklist came back and it is empty.
        </p>
      )}
      {sequence.kind === 'in-progress' ? (
        <p style={{ marginTop: 'var(--space-10)' }}>
          Next: <strong>{nextGuide?.label ?? sequence.next}</strong>
        </p>
      ) : null}
      {sequence.kind === 'live' ? (
        <p style={{ marginTop: 'var(--space-10)' }}>
          Phibian records this organization as live. Nothing on this screen decided that — the{' '}
          <code>LIVE</code> step is on your organization’s checklist.
        </p>
      ) : null}
      <p className="rn-muted" style={{ marginTop: 'var(--space-10)', fontSize: 13 }}>
        Organization <code>{orgId}</code>
      </p>
    </section>
  );
}

/**
 * The dead end this product actually has, said out loud.
 *
 * See `sequenceState` for the mechanism. It is deliberately not dressed as a
 * retryable error and carries no control: there is nothing an administrator can
 * press that changes it.
 */
function Unstartable() {
  return (
    <section className="rn-card" role="status" data-testid="onb-unstartable">
      <h2 className="rn-card-title">
        <Icon name="lock" /> This checklist cannot be started from here
      </h2>
      <p className="rn-muted" style={{ marginTop: 'var(--space-6)' }}>
        The first step, <code>ORGANIZATION_CREATED</code>, cannot be marked by hand, and the steps
        after it are refused until it is complete. Phibian normally reports it complete for any
        organization that exists — so seeing this means the server did not, and that is a server
        fault rather than something missing from your account.
      </p>
      <p className="rn-muted" style={{ marginTop: 'var(--space-10)' }}>
        Nothing on this screen will change it. Ask Phibian to look at your organization record. The
        rest of this page is still the truthful picture of what is and is not configured.
      </p>
    </section>
  );
}

const PROOF_NOTE: Record<StepProof, string> = {
  automatic: 'Complete because your organization exists. It cannot be marked by hand.',
  checked: 'Phibian checks this against your records before recording it.',
  'checked-when-connected':
    'Phibian checks this where the underlying system is connected, and takes your word for it where it is not.',
  attested: 'Nothing is checked. Marking this records that you say so.',
};

function StepRow({
  ordinal,
  item,
  guide,
  isNext,
  mayAdvance,
  pending,
  outcome,
  onAdvance,
}: {
  ordinal: number;
  item: onboarding.ChecklistItem;
  guide: ReturnType<typeof stepGuide>;
  isNext: boolean;
  mayAdvance: boolean;
  pending: boolean;
  outcome: WriteOutcome | undefined;
  onAdvance: () => void;
}) {
  const navigate = useNav();
  const completedAt = item.completedAt;
  const done = completedAt !== null;
  const href = guide?.href;
  return (
    <li className="onb-step" data-state={done ? 'done' : isNext ? 'next' : 'later'}>
      <span className="onb-step-ord" aria-hidden="true">
        {done ? <Icon name="check" size={14} /> : ordinal}
      </span>
      <div className="onb-step-body">
        <div className="onb-step-head">
          <span className="onb-step-label">{guide?.label ?? item.step}</span>
          {done ? (
            <Pill variant="good" dot>
              Recorded {new Date(completedAt).toLocaleDateString()}
            </Pill>
          ) : isNext ? (
            <Pill variant="sky" dot>
              Next
            </Pill>
          ) : (
            <Pill variant="plain" dot>
              Not yet
            </Pill>
          )}
        </div>
        <p className="rn-muted onb-step-meaning">
          {guide?.meaning ??
            `This organization’s checklist carries a step this version of Phibian has no description for. Its key is ${item.step}.`}
        </p>
        {!done && guide ? <p className="onb-step-proof">{PROOF_NOTE[guide.proof]}</p> : null}
        {done && item.completedBy === null ? (
          <p className="onb-step-proof">Recorded by Phibian, not by a person.</p>
        ) : null}

        <div className="onb-step-actions">
          {href !== undefined && !done ? (
            <button type="button" className="rn-link" onClick={() => navigate(href)}>
              Go there <Icon name="arrowRight" size={12} />
            </button>
          ) : null}
          {mayAdvance ? (
            <button
              type="button"
              className="signin-devbtn"
              style={{ width: 'auto', padding: 'var(--space-8) var(--space-14)' }}
              disabled={pending}
              onClick={onAdvance}
            >
              {pending ? 'Recording…' : 'Mark this done'}
            </button>
          ) : null}
        </div>

        {outcome ? <StepOutcome outcome={outcome} onRetry={onAdvance} /> : null}
      </div>
    </li>
  );
}

/**
 * What happened to the last attempt on THIS step.
 *
 * Every branch says the same thing about state — nothing was recorded — and a
 * different thing about the remedy, because the remedy is what differs. The
 * refusal gets no retry: asking again repeats it.
 */
function StepOutcome({ outcome, onRetry }: { outcome: WriteOutcome; onRetry: () => void }) {
  switch (outcome.kind) {
    case 'refused':
      return <Refused heading="Not yours to advance" message={refusalMessage(outcome.error)} />;
    case 'signed-out':
      return (
        <ErrorPanel message="Your sign-in has ended, so this step was not recorded. Sign in again — trying again here will not record it." />
      );
    case 'not-yet':
      return (
        <div className="onb-outcome" data-tone="look" role="status">
          <p>
            <strong>Not recorded.</strong> The server checked and the condition for this step is not
            true yet:
          </p>
          {/* The server's own sentence, verbatim: it names the condition, which
              is the one thing this screen could not work out for itself. */}
          <p className="onb-outcome-server">{outcome.message}</p>
          <p className="rn-muted">
            Do that, then mark it again.{' '}
            <button type="button" className="rn-link" onClick={onRetry}>
              Try again
            </button>
          </p>
        </div>
      );
    case 'unconfirmed':
      return (
        <div className="onb-outcome" data-tone="unsure" role="status">
          <p>
            <strong>Not confirmed.</strong> The request was accepted, and your organization’s
            checklist still does not show this step as done.
          </p>
          <p className="rn-muted">
            This row shows what the server reports, never what was clicked, so it stays outstanding.
            Try again — and if it keeps saying this, tell Phibian rather than treating the step as
            finished.{' '}
            <button type="button" className="rn-link" onClick={onRetry}>
              Try again
            </button>
          </p>
        </div>
      );
    case 'failed':
      return (
        <ErrorPanel
          message="We couldn’t record this step just now, and it has not been recorded. This looks like a connection problem rather than an answer about your organization."
          onRetry={onRetry}
        />
      );
  }
}
